// ── Atomic move writes to Firebase ──

import { ref, update } from 'firebase/database';
import { getFirebaseDb } from '../firebase';
import type {
  GameState,
  PlayerSlot,
  PlayerColor,
  HistoryEntry,
  LacunaRegion,
  TurnPhase,
} from '../game/state';
import { drawPair, canDraw } from '../game/bag';
import { computeScores } from '../game/scoring';
import { applyMove, isGameOver } from '../game/rules';

/** Write the tension choice to Firebase */
export async function writeTensionChoice(
  gameId: string,
  state: GameState,
  chosenToken: number,
): Promise<void> {
  const db = getFirebaseDb();
  const pair = drawPair(state.bag.sequence, state.bag.drawIndex);
  if (!pair) throw new Error('No tokens left');

  const updates: Record<string, unknown> = {
    'turn/drawnTokens': [pair.token1, pair.token2],
    'turn/chosenTension': chosenToken,
    'turn/phase': 'chooseK' as TurnPhase,
    'bag/drawIndex': pair.nextIndex,
  };

  await update(ref(db, `games/${gameId}`), updates);
}

/** Write the k choice to Firebase */
export async function writeKChoice(
  gameId: string,
  k: number,
): Promise<void> {
  const db = getFirebaseDb();
  const updates: Record<string, unknown> = {
    'turn/chosenK': k,
    'turn/phase': 'placeNode' as TurnPhase,
  };
  await update(ref(db, `games/${gameId}`), updates);
}

/** Write a complete move (node placement + strands) to Firebase */
export async function writeMove(
  gameId: string,
  state: GameState,
  nodeX: number,
  nodeY: number,
  targets: string[],
  strandWaypoints?: Array<Array<{ x: number; y: number }> | undefined>,
): Promise<void> {
  const db = getFirebaseDb();

  const activeSlot = state.turn.activePlayer;
  const activePlayer = state.meta[activeSlot];
  if (!activePlayer) throw new Error('No active player');

  const color = activePlayer.color;
  const turnNumber = state.turn.number;

  // Apply the move
  const result = applyMove(state.board, nodeX, nodeY, targets, color, turnNumber, strandWaypoints, state.lacunae);

  // Build atomic update
  const updates: Record<string, unknown> = {};

  // New node
  updates[`board/vertices/${result.newNodeId}`] =
    result.newBoard.vertices[result.newNodeId];

  // New strands
  for (const sid of result.newStrandIds) {
    updates[`board/strands/${sid}`] = result.newBoard.strands[sid];
  }

  // Updated socket counts
  for (const targetId of targets) {
    updates[`board/vertices/${targetId}/free`] =
      result.newBoard.vertices[targetId].free;
  }

  // Sealed lacunae
  for (const lac of result.sealedLacunae) {
    const lacunaData: LacunaRegion = {
      id: lac.id,
      color: lac.color,
      boundaryLength: lac.boundaryLength,
      turn: turnNumber,
      boundaryStrands: lac.boundaryStrands,
    };
    updates[`lacunae/${lac.id}`] = lacunaData;
  }

  // Update scores
  const allLacunae = { ...state.lacunae };
  for (const lac of result.sealedLacunae) {
    allLacunae[lac.id] = {
      id: lac.id,
      color: lac.color,
      boundaryLength: lac.boundaryLength,
      turn: turnNumber,
      boundaryStrands: lac.boundaryStrands,
    };
  }
  const newScores = computeScores(allLacunae);
  updates['scores'] = newScores;

  // History entry
  const historyEntry: HistoryEntry = {
    turn: turnNumber,
    player: activeSlot,
    tension: {
      drawn: state.turn.drawnTokens as [number, number],
      kept: state.turn.chosenTension!,
    },
    k: targets.length,
    node: result.newNodeId,
    strands: result.newStrandIds,
    sealed: result.sealedLacunae.map(l => l.id),
  };
  const historyIndex = state.history ? state.history.length : 0;
  updates[`history/${historyIndex}`] = historyEntry;

  // Check game end: no legal moves OR bag is empty
  const noMoves = isGameOver(result.newBoard);
  const newDrawIndex = state.bag.drawIndex; // already advanced by writeTensionChoice
  const bagEmpty = !canDraw(state.bag.sequence, newDrawIndex);
  const gameOver = noMoves || bagEmpty;

  if (gameOver) {
    updates['meta/status'] = 'finished';
    updates['turn/phase'] = 'sealing';
  } else {
    // Advance turn
    const nextPlayer: PlayerSlot =
      activeSlot === 'player1' ? 'player2' : 'player1';
    updates['turn/number'] = turnNumber + 1;
    updates['turn/activePlayer'] = nextPlayer;
    updates['turn/phase'] = 'chooseTension';
    updates['turn/drawnTokens'] = null;
    updates['turn/chosenTension'] = null;
    updates['turn/chosenK'] = null;
    updates['turn/placedNode'] = null;
    updates['turn/drawnStrandTargets'] = [];
  }

  await update(ref(db, `games/${gameId}`), updates);
}

/** Write a pie rule swap */
export async function writePieSwap(
  gameId: string,
  state: GameState,
  doSwap: boolean,
): Promise<void> {
  const db = getFirebaseDb();
  const updates: Record<string, unknown> = {
    'meta/pieRuleAvailable': false,
  };

  if (doSwap && state.meta.player1 && state.meta.player2) {
    // Swap colors
    updates['meta/player1/color'] = state.meta.player2.color;
    updates['meta/player2/color'] = state.meta.player1.color;
  }

  await update(ref(db, `games/${gameId}`), updates);
}
